import { BrowserRouter, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import './App.css';
import LayoutProvider, { LayoutContext } from './context';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Button, CssVarsProvider, extendTheme, Typography } from '@mui/joy';

import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import { AnimatePresence, motion } from 'framer-motion';
import PhotoBookEditor from './content/PhotoBookEditor';
import { bookApi } from './api';
import { logEvent } from './logger';
import { enableFitImageMode } from './fitImage';
import { enableConverterMode } from './heicConverter';
import { enableAdminViewMode } from './adminViewMode';
import { captureBypassFromUrl, checkMaintenance } from './maintenance';
import AdminApp from './admin/AdminApp';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/autoplay';
import ModalOnboarding from './components/models/ModalOnboarding';
import VpnAlert from './components/VpnAlert';
import { PaymentFail, PaymentSuccess, GiftPaymentSuccess, GiftPaymentFail } from './content/PaymentResult';




function AnimatePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { account, showOnboarding, setShowOnboarding, reloadCart } = useContext(LayoutContext);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmSource   = params.get("utm_source");
    const utmMedium   = params.get("utm_medium");
    const utmCampaign = params.get("utm_campaign");
    const utmTerm     = params.get("utm_term");
    const utmContent  = params.get("utm_content");

    // Отправляем только если есть хотя бы один UTM-параметр
    if (utmSource || utmMedium || utmCampaign || utmTerm || utmContent) {
      // Проверяем, не отправляли ли уже эту комбинацию
      const utmKey = `${utmSource}|${utmMedium}|${utmCampaign}|${utmTerm}|${utmContent}`;
      const sent = sessionStorage.getItem("utm_sent");
      if (sent !== utmKey) {
        bookApi.trackUtm({ utmSource, utmMedium, utmCampaign, utmTerm, utmContent })
          .then(() => sessionStorage.setItem("utm_sent", utmKey))
          .catch(err => console.warn("UTM track error:", err));
      }
    }
  }, []);

  useEffect(() => {
    logEvent({
      source: "lifecycle",
      event: "route_change",
      message: `Открыл ${location.pathname}${location.search}`,
    });
  }, [location.pathname, location.search]);

  const handleOnboardingClose = () => {
    localStorage.setItem("first_start", "1");
    setShowOnboarding(false);
  };

  const theme = extendTheme({
    colorSchemes: {
      light: {
        palette: {
          primary: {
            50:  '#f8f8f8', // Почти белый, для очень светлых подложек
            100: '#f0f0f0', // Soft-вариант (фон кнопок или тегов)
            200: '#e5e5e5', // Ховер для soft-варианта
            300: '#d4d4d4', // Границы (outlined) — спокойный серый
            400: '#737373', // Вспомогательный текст или иконки
            500: '#171717', // Основной цвет (акцентный черный/антрацит)
            600: '#262626', // Ховер для solid (чуть светлее или темнее в зависимости от инверсии)
            700: '#0a0a0a', // Цвет при нажатии (почти чистый черный)
            800: '#050505', 
            900: '#000000', 
          },
          danger: {
            50: "#fde3e4",
            100: "#facccd",
            200: "#f49999",
            300: "#e0666b",
            400: "#cc3340",
            500: "#c30013",
            600: "#b10011",
            700: "#9e000e",
            800: "#8b000b",
            900: "#780008"
          },
          success: {
            50: "#F6FEF6",
            100: "#E3FBE3",
            200: "#C7F7C7",
            300: "#A1E8A1",
            400: "#51BC51",
            500: "#1F7A1F",
            600: "#136C13",
            700: "#0A470A",
            800: "#042F04",
            900: "#021D02"
          },
          neutral: {
            500: '#242424', // Цвет фона для всех вариантов
            600: '#2e2e2e', // Цвет фона при наведении если solid вариант
            700: '#1f1f1f', // Цвет фона для всех вариантов (при нажатии)
          },
        },
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
        
        extrasmall: 300,
        small: 500,
        medium: 800,
        large: 1100,
      },
    },
    components: {
      JoyButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: '.2s',
            borderRadius: theme.vars.radius.lg,
            "&:hover": {
              transform: "scale(1.02)"
            }
          })
        }
      },
      // JoyTextarea: {
      //   styleOverrides: {
      //     root: ({ theme }) => ({
      //       borderRadius: theme.vars.radius.lg
      //     })
      //   }
      // },
      // JoyInput: {
      //   styleOverrides: {
      //     root: ({ theme }) => ({
      //       borderRadius: theme.vars.radius.lg
      //     })
      //   }
      // },
      // JoySelect: {
      //   styleOverrides: {
      //     root: ({ theme }) => ({
      //       borderRadius: theme.vars.radius.lg
      //     })
      //   }
      // },
      // JoyAutocomplete: {
      //   styleOverrides: {
      //     root: ({ theme }) => ({
      //       borderRadius: theme.vars.radius.lg
      //     })
      //   }
      // },

      
      // JoyIconButton: {
      //   styleOverrides: {
      //     root: ({ theme }) => ({
      //       borderRadius: theme.vars.radius.lg
      //     })
      //   }
      // }
    },
  });
  

  // ── Проверка возврата с Робокассы ──
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get("payment");

    if (paymentStatus === "success") {
      const orderId = localStorage.getItem("pending_order_id");
      const orderNumber = localStorage.getItem("pending_order_number");

      if (orderId) {
        // Polling: проверяем статус оплаты каждые 2 секунды
        let attempts = 0;
        const maxAttempts = 15; // 30 секунд максимум

        const checkPayment = async () => {
          try {
            const status = await bookApi.checkPaymentStatus(orderId);
            if (status.paid) {
              setPaymentOrder(status);
              setShowPaymentSuccess(true);
              reloadCart();
              localStorage.removeItem("pending_order_id");
              localStorage.removeItem("pending_order_number");
              // Убираем параметры из URL
              window.history.replaceState({}, "", "/");
              return;
            }
          } catch (e) {
            console.warn("Payment check error:", e);
          }
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(checkPayment, 2000);
          } else {
            // Таймаут — показываем всё равно (оплата могла пройти)
            setPaymentOrder({ order_number: orderNumber, order_id: orderId });
            setShowPaymentSuccess(true);
            localStorage.removeItem("pending_order_id");
            localStorage.removeItem("pending_order_number");
            window.history.replaceState({}, "", "/");
          }
        };
        checkPayment();
      }
    }

    if (paymentStatus === "fail") {
      alert("Оплата не была завершена. Вы можете попробовать снова.");
      window.history.replaceState({}, "", "/");
    }
  }, []);


  return (
    <CssVarsProvider
      theme={theme}
    >
      <AnimatePresence mode='wait'>
        {/* Ключ по ВЕРХНЕУРОВНЕВОМУ разделу, а не по полному пути:
            переходы между вкладками внутри /admin больше не перемонтируют
            <AdminApp/> (сайдбар и авторизация остаются живыми) — обновляется
            только основная область. Полный ремоунт остаётся лишь при смене
            раздела (сайт ↔ /admin ↔ страницы оплаты). */}
        <Routes location={location} key={location.pathname.split("/")[1] || "root"}>
          
          <Route path='/' >
            <Route path="" element={<PhotoBookEditor />} />
          </Route>

          {/* Вход под клиентом по токену из техподдержки */}
          <Route path="/user/:token" element={<UserRestore />} />

          {/* Страницы возврата с оплаты */}
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-fail" element={<PaymentFail />} />
          <Route path="/gift-success" element={<GiftPaymentSuccess />} />
          <Route path="/gift-fail" element={<GiftPaymentFail />} />

          {/* Админка */}
          <Route path="/admin/*" element={<AdminApp />} />
        </Routes>

        <ModalOnboarding
          open={showOnboarding}
          onClose={handleOnboardingClose}
        />

        <VpnAlert />

        {/* Модалка успешной оплаты */}
        {showPaymentSuccess && paymentOrder && (
          <Modal open={showPaymentSuccess} onClose={() => setShowPaymentSuccess(false)}>
            <ModalDialog sx={{ maxWidth: 420, p: 3, borderRadius: "20px", textAlign: "center" }}>
              <Box sx={{
                width: 64, height: 64, borderRadius: "50%", mx: "auto", mb: 2,
                bgcolor: "success.50",
                display: "flex", alignItems: "center", justifyContent: "center"
              }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 36, color: "success.500" }} />
              </Box>
              <Typography level="title-lg" fontWeight={800}>
                Оплата прошла успешно! 🎉
              </Typography>
              <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
                Заказ <strong>#{paymentOrder.order_number}</strong> оплачен.
              </Typography>
              {paymentOrder.total_price && (
                <Typography level="body-sm" sx={{ mt: 0.5 }}>
                  Сумма: <strong>{parseFloat(paymentOrder.total_price).toLocaleString("ru-RU")} ₽</strong>
                </Typography>
              )}
              <Typography level="body-xs" color="neutral" sx={{ mt: 1.5 }}>
                Мы отправили подтверждение на вашу почту.
                Скоро свяжемся для уточнения деталей.
              </Typography>
              <Button
                fullWidth size="lg" color="success" sx={{ mt: 3, borderRadius: "12px" }}
                onClick={() => setShowPaymentSuccess(false)}
              >
                Отлично!
              </Button>
            </ModalDialog>
          </Modal>
        )}
      </AnimatePresence>
    </CssVarsProvider>
  );
}


function UserRestore() {
  const { token } = useParams();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        try {
          const _p = new URLSearchParams(window.location.search);
          if (_p.has("fit_image")) enableFitImageMode();
          if (_p.has("converter")) enableConverterMode();
          if (_p.has("admin")) enableAdminViewMode();
        } catch (e) {}
        const data = await bookApi.restoreUser(token);
        if (cancelled) return;
        if (data.client_uid) localStorage.setItem("client_uid", data.client_uid);
        if (Array.isArray(data.book_ids)) {
          localStorage.setItem("photobook_ids", JSON.stringify(data.book_ids));
        }
        localStorage.setItem("first_start", "1"); // не показывать онбординг
      } catch (e) {
        console.warn("restoreUser error:", e);
      } finally {
        // Полная перезагрузка, чтобы контекст перечитал localStorage
        window.location.replace("/");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <Box sx={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography level="title-md">Восстанавливаем вашу сессию…</Typography>
    </Box>
  );
}

const MB_LOGO_URL = "/logo.png";

function MaintenanceOverlay() {
  const P = { fontSize: "clamp(15px,2.4vw,18px)", lineHeight: 1.55, margin: "0 0 18px", color: "#eef7fd" };
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 2147483647, background: "#a6d3ea", color: "#fff",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      textAlign: "center", padding: 24, WebkitFontSmoothing: "antialiased",
      fontFamily: "system-ui,-apple-system,Segoe UI,Roboto,sans-serif",
    }}>
      <div style={{ maxWidth: 560 }}>
        <h1 style={{ fontSize: "clamp(22px,4vw,30px)", fontWeight: 800, letterSpacing: 0.3, margin: "0 0 28px", color: "#f7fcff" }}>
          МЫ ГОТОВИМ ДЛЯ ВАС КОЕ-ЧТО ВАЖНОЕ🤍
        </h1>
        <p style={P}>Сейчас команда <b>Magic Board</b> обновляет конструктор, чтобы создавать Фотокниги стало ещё удобнее и приятнее.</p>
        <p style={P}>Не переживайте — все ваши созданные книги сохранены и останутся в разделе «Мои книги».</p>
        <p style={{ ...P, margin: 0 }}>Совсем скоро вернёмся 🕊️</p>
      </div>
      <img src={MB_LOGO_URL} alt="Magic Board"
           onError={(e) => { e.currentTarget.style.display = "none"; }}
           style={{ position: "absolute", bottom: 28, height: 34, opacity: 0.9 }} />
    </div>
  );
}

function MaintenanceGate({ children }) {
  const [state, setState] = useState("loading"); // loading | app | maintenance
  useEffect(() => {
    // админку не блокируем (свой вход у стаффа)
    if (window.location.pathname.startsWith("/admin")) { setState("app"); return; }
    captureBypassFromUrl();
    let cancelled = false;
    checkMaintenance().then(({ allowed }) => { if (!cancelled) setState(allowed ? "app" : "maintenance"); });
    const onMaint = () => { if (!cancelled) setState("maintenance"); };
    window.addEventListener("mb-maintenance", onMaint);
    return () => { cancelled = true; window.removeEventListener("mb-maintenance", onMaint); };
  }, []);
  if (state === "loading") return null;            // короткий бланк, приложение не монтируем
  if (state === "maintenance") return <MaintenanceOverlay />;
  return children;
}


function App() {
  return (
    <MaintenanceGate>
      <BrowserRouter>
        <LayoutProvider>
          <AnimatePage/>
        </LayoutProvider>
      </BrowserRouter>
    </MaintenanceGate>
  );
}

export default App;